import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { parsePhoneNumber } from 'react-phone-number-input';
import PropTypes from 'prop-types';

import { useLocation } from 'react-router-dom';
import * as queryString from 'query-string';
import requireAuth from '../../components/hocs/requireAuth';
import SettingsForm from '../../components/SettingsForm';
import * as settingsActions from '../../actions/settings-actions';
import { fetchAuthenticatedUser, updateMatchPlayAvailability } from '../../actions';
import { ageSliderDictionary } from '../../utils/constants';
import { getApiErrorMessage } from '../../utils/apiError';

import styles from './settingsPage.scss';
import { showErrorPopup } from '../../actions/notification-actions';
import { useStatsig } from '../../components/hooks/useStatsig';
import { getLinkedAccounts } from '../../api/settingsApi';
import { getApiAxiosInstance } from '../../utils/network';
import { linkedAccountTypes } from '../../utils/oauth';

const SettingsPage = (props) => {
    const [submitting, setSubmitting] = useState(false);
    const [apiEmailError, setApiEmailError] = useState(null);
    const [linkedAccounts, setLinkedAccounts] = useState([]);
    const [isTennisAustraliaAccountLinked, setIsTennisAustraliaAccountLinked] = useState(false);
    const [isAppleAccountLinked, setIsAppleAccountLinked] = useState(false);
    const [isFacebookAccountLinked, setIsFacebookAccountLinked] = useState(false);
    const [linkedAccountsLoaded, setLinkedAccountsLoaded] = useState(false);
    const auth = useSelector(({ auth }) => auth);
    const dispatch = useDispatch();
    const location = useLocation();
    const parsed = queryString.parse(location.search);
    const ACCT_ALREADY_LINKED_ERROR = 'acct_already_linked';
    const { checkGate, featureGates } = useStatsig();
    const isTennisAustraliaLoginActive = checkGate(featureGates.TENNIS_AUSTRALIA_OAUTH);

    const { history } = props;
    const { user, feedPreferences, isLoadingFeedPreferences } = auth || {};

    useEffect(() => {
        const shouldFetchFeedPreferences = user && !feedPreferences && !isLoadingFeedPreferences;

        dispatch(settingsActions.getAccountSettings());

        if (shouldFetchFeedPreferences) {
            dispatch(settingsActions.getFeedPreferences());
        }
        if (user?.isAustralian) {
            dispatch(settingsActions.getAustraliaSettings());
        }
    }, [user, feedPreferences, isLoadingFeedPreferences]);

    const fetchLinkedAccounts = async () => {
        try {
            const axiosInstance = getApiAxiosInstance();
            const accountLinkResponse = await getLinkedAccounts(axiosInstance);
            const accounts = accountLinkResponse?.data?.memberThirdPartyAccounts || [];
            setLinkedAccounts(accounts);
            setIsTennisAustraliaAccountLinked(accounts.some(acc => acc.type === linkedAccountTypes.TENNIS_AUSTRALIA));
            setIsAppleAccountLinked(accounts.some(acc => acc.type === linkedAccountTypes.APPLE));
            setIsFacebookAccountLinked(accounts.some(acc => acc.type === linkedAccountTypes.FACEBOOK));
        } catch (error) {
            dispatch(showErrorPopup({ message: 'Error checking linked accounts', showSupportLink: true }));
        } finally {
            setLinkedAccountsLoaded(true);
        }
    };

    useEffect(() => {
        fetchLinkedAccounts();
    }, []);

    useEffect(() => {
        if (parsed?.error) {
             if (parsed?.error === ACCT_ALREADY_LINKED_ERROR) {
                dispatch(showErrorPopup({ message: 'This account is already linked to another UTR Sports account', showSupportLink: true }));
            } else {
                dispatch(showErrorPopup({ showSupportLink: true }));
            }
        }
    }, [parsed?.error]);

    const onEmailChange = () => {
        setApiEmailError(null);
    };

    const handleUpdateMatchPlayAvailability = (val) => {
        const data = [{
            playAvailability: val ? 'all' : 'none',
        }];
        dispatch(updateMatchPlayAvailability(data));
    };

    const processAccountSettings = async (values, settingsUpdatedStatus) => {
        const accountSettingsUpdated = await dispatch(settingsActions.updateAccountSettings(values));
        settingsUpdatedStatus.isUpdated = !accountSettingsUpdated?.response;
        if (!settingsUpdatedStatus.isUpdated) {
            settingsUpdatedStatus.error = getApiErrorMessage(accountSettingsUpdated);
        }
    };

    const processFeedPreferencesSettings = async (values, feedPreferencesUpdatedStatus) => {
        const feedPreferencesUpdated = await dispatch(settingsActions.updateFeedPreferences(values));
        feedPreferencesUpdatedStatus.isUpdated = !feedPreferencesUpdated?.response;
        if (!feedPreferencesUpdatedStatus.isUpdated) {
            feedPreferencesUpdatedStatus.error = getApiErrorMessage(feedPreferencesUpdated);
        }
    };

    const processAustraliaSettings = async (tennisConnectId, marketingEmailsOptIn, isAustralian) => {
        if (isAustralian) {
            const australiaSettings = {
                tennisConnectId,
                marketingEmailsOptIn,
            };
            const updateTennisConnect = await dispatch(settingsActions.updateAustraliaSettings(australiaSettings));
            return updateTennisConnect;
        }
        return true;
    };

    const handleSubmit = async (values) => {
        const updatingValues = {};
        let apiEmailError = null;

        updatingValues.currentPassword = values.currentPassword;
        if (values.email !== auth.user.email) {
            updatingValues.email = values.email;
            dispatch(settingsActions.updateEmailConfirmedAfterChangedEmail());
        }
        if (values.phone !== auth.user.phone) {
            updatingValues.phone = values.phone;
            if (values.phone) {
                const parsed = parsePhoneNumber(values.phone);
                const country = parsed?.country ?? values.phoneCountryCode ?? null;
                updatingValues.phoneCountryCode = country;
            }
        }
        if (values.password && values.password.length > 0 && values.confirmPassword && values.confirmPassword.length > 0) {
            updatingValues.password = values.password;
            updatingValues.confirmPassword = values.confirmPassword;
        }
        if (values.secondaryEmail !== auth.user.secondaryEmail) {
            updatingValues.secondaryEmail = values.secondaryEmail;
        }
        if (auth.user.secondaryEmail && updatingValues.secondaryEmail === '') {
            updatingValues.isSecondaryEmailRemoved = true;
        }
        if (values.smsOptIn !== auth?.settings?.smsOptIn) {
            updatingValues.smsOptIn = values.smsOptIn;
        }
        setSubmitting(true);

        const feedPreferencesValues = {
            resultsCountToMyUtr: values.resultsCountToMyUtr,
            recreationalEvents: values.recreationalEvents,
            nonScoringEvents: values.nonScoringEvents,
            gameLocation: values.gameLocation,
            distance: values.distance,
            gender: values.gender,
            utrMin: values.utrRange[0],
            utrMax: values.utrRange[1],
            showPickleballContent: values.showPickleballContent,
            showTennisContent: values.showTennisContent,
        };
        feedPreferencesValues.ageMin = ageSliderDictionary[values.ageRange[0]];
        feedPreferencesValues.ageMax = ageSliderDictionary[values.ageRange[1]];
        const settingsUpdatedStatus = {
            isUpdated: false,
            error: '',
        };
        const feedPreferencesUpdatedStatus = {
            isUpdated: false,
            error: '',
        };
        const { isAustralian, australiaTennisConnectId } = auth.user;

        let australiaSettingsUpdated = false;
        let isValidateTennisAustraliaId = false;
        if (isAustralian && !australiaTennisConnectId) {
            isValidateTennisAustraliaId = await dispatch(settingsActions.validateTennisConnectId(values.tennisConnectId));
        } else {
            isValidateTennisAustraliaId = true;
        }
        if (isValidateTennisAustraliaId) {
            await processFeedPreferencesSettings(feedPreferencesValues, feedPreferencesUpdatedStatus);
            if (feedPreferencesUpdatedStatus.isUpdated) {
                await processAccountSettings(updatingValues, settingsUpdatedStatus);
                if (settingsUpdatedStatus.isUpdated) {
                    australiaSettingsUpdated = await processAustraliaSettings(values.tennisConnectId, values.marketingEmailsOptIn, isAustralian);
                }
            }
        }

        if (settingsUpdatedStatus.isUpdated && feedPreferencesUpdatedStatus.isUpdated && (!isAustralian || (isAustralian && australiaSettingsUpdated))) {
           dispatch(fetchAuthenticatedUser());
        } else {
            apiEmailError = feedPreferencesUpdatedStatus.error || settingsUpdatedStatus.error;
        }
        setSubmitting(false);
        setApiEmailError(apiEmailError);
    };

    const { isAustralian } = auth.user;

    return (
        <div className={`${styles.section}`}>
            <div className={`container h2 bg-transparent`}>
                <h2 className="text-center" data-testid="settings.page.title">Settings</h2>
            </div>
            {(auth && auth.settings && auth.feedPreferences && linkedAccountsLoaded && ((isAustralian && auth.australiaSettings) || !isAustralian)) && (
                <SettingsForm
                    auth={auth}
                    handleSubmit={handleSubmit}
                    submitting={submitting}
                    onEmailChange={onEmailChange}
                    apiEmailError={apiEmailError}
                    updateMatchPlayAvailability={handleUpdateMatchPlayAvailability}
                    showErrorPopup={({ message, title }) => dispatch(showErrorPopup({ message, title, showSupportLink: true }))}
                    isTennisAustraliaLoginActive={isTennisAustraliaLoginActive}
                    linkedAccounts={linkedAccounts}
                    isTennisAustraliaAccountLinked={isTennisAustraliaAccountLinked}
                    isAppleAccountLinked={isAppleAccountLinked}
                    isFacebookAccountLinked={isFacebookAccountLinked}
                    onLinkedAccountsChange={fetchLinkedAccounts}
                />
            )}
        </div>
    );
};

SettingsPage.propTypes = {
    history: PropTypes.shape({
        length: PropTypes.number.isRequired,
        goBack: PropTypes.func.isRequired,
    }).isRequired,
    auth: PropTypes.shape({
        user: PropTypes.shape({
            australiaTennisConnectId: PropTypes.string,
            isAustralian: PropTypes.bool,
            email: PropTypes.string,
            phone: PropTypes.string,
            secondaryEmail: PropTypes.string,
        }),
        feedPreferences: PropTypes.instanceOf(Object).isRequired,
        isLoadingFeedPreferences: PropTypes.bool.isRequired,
    }).isRequired,
};

export default requireAuth(SettingsPage);
